import { isPlatformServer } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import type {
  ActivatedRouteSnapshot,
  CanActivate,
  RouterStateSnapshot,
} from '@angular/router';
import { AuthServiceInterface } from '@freelancer/auth/interface';
import { Location } from '@freelancer/location';
import { LOGIN_AUTH_SERVICE } from './login.config';

@Injectable({
  providedIn: 'root',
})
export class AuthCallbackGuard implements CanActivate {
  constructor(
    private location: Location,
    @Inject(PLATFORM_ID) private platformId: Object,
    @Inject(LOGIN_AUTH_SERVICE) private authService: AuthServiceInterface,
  ) {}

  async canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    // We render the empty component on the server as we obviously can't set
    // the session there: once returned, the app will bootstrap on the same
    // route, and this guard will be executed again
    if (isPlatformServer(this.platformId)) {
      return true;
    }
    const { uid, token, next } = route.queryParams;
    /**
     * Token is alphanumeric and shouldn't require to be decoded. However, this
     * will be kept there as decoding an alphanumeric token is harmless when it
     * yields the exact same result anyway.
     */
    this.authService.setSession(uid, decodeURIComponent(token));
    /**
     * URLs do not require decoding, as the browser would know when to decode.
     *
     * If URLs are decoded, it causes issues where the queryParams of a target
     * URL would be consumed by linkshim instead of the target URL.
     */
    await this.location.navigateByUrl(next);
    return false;
  }
}
